import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Box, Typography, Paper, CircularProgress, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { DataGrid, GridRowSelectionModel, GridRenderCellParams } from '@mui/x-data-grid';

interface InventoryGroup {
  id: string;
  name: string;
  ids: string[];
}

interface Asset {
  id: string;
  customId: string;
  operatorCompany: string;
  assetType: string;
  width: number;
  height: number;
  loopSettings: string;
  status: string;
  dateAdded: string;
  latitude: number;
  longitude: number;
  address: string;
  group: string;
  dailyImpression?: number;
  polygon: any;
  traffic: any[];
}

const ManageGroups: React.FC = () => {
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    const fetchGroupsAndAssets = async () => {
      setLoading(true);
      const groupSnap = await getDocs(collection(db, 'InventoryGroups'));
      setGroups(groupSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryGroup)));
      const assetSnap = await getDocs(collection(db, 'Assets'));
      setAssets(assetSnap.docs.map(doc => ({
        id: doc.id,
        customId: doc.data().customId || '',
        ...doc.data()
      } as Asset)));
      setLoading(false);
    };
    fetchGroupsAndAssets();
  }, []);

  const selectedGroup = groups.find(g => g.id === selectedGroupId);
  const groupAssets = selectedGroup
    ? assets.filter(a => selectedGroup.ids.includes(a.id))
    : [];

  const handleRemoveInventory = async () => {
    if (!selectedGroup) return;
    setRemoving(true);
    const newIds = selectedGroup.ids.filter(id => !selectionModel.includes(id));
    await updateDoc(doc(db, 'InventoryGroups', selectedGroup.id), { ids: newIds });
    setGroups(groups.map(g => g.id === selectedGroup.id ? { ...g, ids: newIds } : g));
    setSelectionModel([]);
    setRemoving(false);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 4 }}>
      <Typography variant="h4" mb={3}>Manage Inventory Groups</Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <FormControl sx={{ minWidth: 300, mb: 3 }} size="small">
            <InputLabel id="group-select-label">Select Group</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroupId}
              label="Select Group"
              onChange={e => setSelectedGroupId(e.target.value)}
            >
              {groups.map(group => (
                <MenuItem key={group.id} value={group.id}>{group.name} ({group.ids.length} inventory items)</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedGroup && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  disabled={selectionModel.length === 0 || removing}
                  onClick={handleRemoveInventory}
                >
                  Remove Inventory Item(s)
                </Button>
              </Box>
              <Paper sx={{ width: '100%', height: 500 }}>
                <DataGrid
                  rows={groupAssets}
                  columns={[
                    { field: 'id', headerName: 'System ID', width: 120 },
                    { field: 'customId', headerName: 'Custom ID', width: 120 },
                    { field: 'operatorCompany', headerName: 'Operator', width: 150 },
                    { field: 'assetType', headerName: 'Type', width: 120 },
                    { field: 'facingDirection', headerName: 'Facing Direction', width: 140 },
                    {
                      field: 'dimensions',
                      headerName: 'Dimensions',
                      width: 140,
                      renderHeader: () => (
                        <span title="Dimensions are in feet (ft). Format: Height x Width.">Dimensions</span>
                      ),
                      renderCell: (params: GridRenderCellParams<Asset>) => {
                        const w = params.row.width;
                        const h = params.row.height;
                        return (
                          <span title="Dimensions are in feet (ft). Format: Height x Width.">
                            {(h && w) ? `${h}x${w}` : '-'}
                          </span>
                        );
                      },
                      sortable: false,
                      filterable: false,
                    },
                    { field: 'status', headerName: 'Status', width: 120 },
                    { field: 'address', headerName: 'Address', width: 200 },
                    { field: 'dailyImpression', headerName: 'Daily Impression', width: 150, type: 'number' },
                  ]}
                  checkboxSelection
                  disableRowSelectionOnClick={false}
                  onRowSelectionModelChange={setSelectionModel}
                  rowSelectionModel={selectionModel}
                  sx={{ bgcolor: 'background.paper', color: 'text.primary', border: 0,
                    '& .MuiDataGrid-columnHeaders': {
                      fontWeight: 'bold',
                      fontSize: '1.05rem',
                      backgroundColor: 'background.default',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                />
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ManageGroups; 